import { ChangeEvent, useEffect, useState } from 'react';
import { Chip, TextField, Zoom } from '@material-ui/core';
import { Send } from '@material-ui/icons';

import { AttachFileButton } from '../ViewTickets/AttachFileButton';
import { CcModal } from '../CcModal';
import { TransitionGroup } from 'react-transition-group';

import { CcButton } from '../CcButton';
import {
  AttachmentUpload,
  SupportCreateTicket,
} from '../../../types/commonTypes';
import { xsScreen } from '../../../lib/constants';
import { formatBytes } from '../../../lib/formatters';
import { Tooltip } from '../../../components/Tooltip';
import { Button } from '../../../components/Button/NewButton';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useOperator } from '../../../contexts/OperatorContext';

interface Props {
  ticketFields: SupportCreateTicket;
  loading: boolean;
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  updateFormField(key: keyof SupportCreateTicket, value: any): void;
  onClose(bool: boolean): void;
}

export const CreateTicketResponseBar = ({
  ticketFields,
  loading,
  onChange,
  updateFormField,
}: Props) => {
  const [attachments, setAttachments] = useState<AttachmentUpload[]>([]);
  const [ccModalOpen, setCcModalOpen] = useState(false);
  const [contactList, setContactList] = useState<string[]>([]);
  const isMobile = useMediaQuery(xsScreen);
  const { sites, type } = useOperator();

  const validateFields = (): boolean => {
    if (
      ticketFields.name.trim() !== '' &&
      ticketFields.subject.trim() !== '' &&
      (type === 'Hospitality' && (sites?.length ?? 0) > 1
        ? Boolean(ticketFields.site)
        : true) &&
      Boolean(ticketFields.category) &&
      ticketFields.text.trim() !== '' &&
      (ticketFields.phone.trim() !== '' ? ticketFields.phone.length > 9 : true)
    )
      return true;
    else return false;
  };

  useEffect(() => {
    if (attachments.length == 0) {
      updateFormField('attachment', null);
    } else {
      updateFormField('attachment', {
        filename: attachments[0].filename,
        base64: attachments[0].base64,
      });
    }
  }, [attachments]);

  const handleDelete = (fileName: string) => {
    setAttachments(
      attachments.filter((attachment: any) => attachment.filename !== fileName)
    );
  };

  useEffect(() => {
    updateFormField('cc', contactList.toString());
  }, [contactList]);

  return (
    <>
      <div className="rounded border border-border-light dark:border-border-dark p-2">
        <TextField
          name="text"
          fullWidth
          InputProps={{
            disableUnderline: true,
            minRows: 6,
          }}
          multiline
          maxRows={6}
          value={ticketFields.text}
          onChange={onChange}
          disabled={loading}
        />
      </div>
      <div className="w-full">
        {attachments && attachments.length > 0 ? (
          <TransitionGroup className="flex flex-wrap items-center justify-end min-h-[35px] p-1 my-2">
            {attachments.map((attachment: any, index: number) => {
              return (
                <Zoom key={`attachment-${index + 1}`}>
                  <Tooltip
                    title={formatBytes(attachment.size)}
                    showArrow
                    placement="top"
                  >
                    <Chip
                      className="m-1"
                      label={attachment.filename}
                      onDelete={() => handleDelete(attachment.filename)}
                    />
                  </Tooltip>
                </Zoom>
              );
            })}
          </TransitionGroup>
        ) : null}
      </div>
      <div className="w-full inline-flex flex-wrap items-center mt-2 p-1">
        <CcButton
          badgeContent={contactList.length}
          disabled={loading}
          onClick={() => setCcModalOpen(true)}
        />
        <div className="inline-flex items-center ml-auto">
          {!isMobile ? (
            <AttachFileButton
              attachments={attachments}
              disabled={loading}
              setAttachments={setAttachments}
              limitFileCount={1}
              limitFileSize={10000000}
              limitBuffer={20000}
            />
          ) : null}
          <Button
            className="!w-full sm:!w-fit mt-2 sm:mt-0"
            theme="action"
            startIcon={<Send />}
            disabled={!validateFields() || loading}
            type="submit"
          >
            SUBMIT
          </Button>
        </div>
      </div>
      <CcModal
        ccList={contactList}
        onClose={() => setCcModalOpen(false)}
        open={ccModalOpen}
        updateCcList={setContactList}
        portalOut={document.getElementById('create-ticket-modal') as Element}
        maxWidth="lg"
      />
    </>
  );
};
